import { faFacebookF } from '@fortawesome/free-brands-svg-icons/faFacebookF'
import { faLinkedinIn } from '@fortawesome/free-brands-svg-icons/faLinkedinIn'
import { faTwitter } from '@fortawesome/free-brands-svg-icons/faTwitter'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight'
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope'
import { faHome } from '@fortawesome/free-solid-svg-icons/faHome'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import loadable from '@loadable/component'
import { graphql, Link } from 'gatsby'
import {
  GatsbySeo,
  ArticleJsonLd,
  BreadcrumbJsonLd,
} from 'gatsby-plugin-next-seo'
import React from 'react'
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share'

import Layout from '../components/Layout'
import EcogestureCard from '../components/EcogestureCard'
import { RichContent } from '../components/Content'
import { Sidebar } from '../components/Sidebar'
import { TipsheetsList } from '../components/TipsheetsList'
import ContentBottomBanner from '../components/Banners/ContentBottomBanner'

const FAQSeo = loadable(() => import('../components/FAQSeo'))
const NewsletterForm = loadable(() => import('../components/NewsletterForm'))
const QuestionAnswerCard = loadable(() => import('../components/QuestionAnswerCard'))

export const query = graphql`query ($id: String!) {
  contentfulEcoGesture(id: { eq: $id }) {
    slug
    publishDate
    updatedAt
    title
    description {
      description
    }
    body {
      raw
      references {
        ... on ContentfulTipsheet {
          contentful_id
          __typename
          slug
          title
          category {
            slug
          }
        }
      }
    }
    tags
    relatedQuestions {
      id
      name
      answer {
        raw
      }
    }
  }
  allContentfulTipsheet(
    filter: {
      node_locale: { eq: "fr" }
    }
    sort: [{ featured: ASC }, { updatedAt: DESC }]
    skip: 0
    limit: 3
  ) {
    edges {
      node {
        id
        slug
        createdAt
        updatedAt
        title
        description {
          description
        }
        category {
          slug
        }
        image {
          gatsbyImageData(
            width: 420
            height: 420
            placeholder: BLURRED
            formats: [AUTO, WEBP, AVIF]
            layout: CONSTRAINED
          )
        }
      }
    }
  }
  allContentfulEcoGesture(
    filter: {node_locale: {eq: "fr"}, id: {ne: $id}}
    sort: {publishDate: DESC}
    skip: 0
    limit: 3
  ) {
    edges {
      node {
        id
        slug
        title
      }
    }
  }
  allContentfulBrand(
    filter: {node_locale: {eq: "fr"}, featured: {eq: true}, indexable: {eq: true}}
    sort: {createdAt: DESC}
    skip: 0
    limit: 2
  ) {
    edges {
      node {
        id
        slug
        name
      }
    }
  }
  allContentfulBlogPost(
    filter: {
      node_locale: { eq: "fr" },
      id: { ne: $id }
    }
    sort: [{ featured: ASC }, { createdAt: DESC }]
    skip: 0
    limit: 3
  ) {
    edges {
      node {
        id
        slug
        title
        category {
          slug
        }
      }
    }
  }
  logo: file(relativePath: { eq: "logo.jpg" }) {
    publicURL
  }
  site {
    siteMetadata {
      title
      siteUrl
      social {
        twitter
      }
    }
  }
}`

const Page = ({
  data: {
    contentfulEcoGesture: ecogesture,
    allContentfulEcoGesture,
    allContentfulTipsheet,
    allContentfulBrand,
    allContentfulBlogPost,
    logo,
    site: {
      siteMetadata: { title: siteName, siteUrl, social },
    },
  },
}) => {
  const slug = `/ecogestes/${ecogesture.slug}/`
  const url = `${siteUrl}${slug}`
  const tags = ecogesture.tags
  const title = ecogesture.title
  const description = ecogesture.description.description
  const publishedDate = ecogesture.publishedDate
  const modifiedDate = ecogesture.updatedAt
  const hashtags = tags.map(tag => `${tag.split(' ').join('')}`)

  return (
    <Layout>
      <GatsbySeo
        title={title}
        description={description}
        canonical={url}
        openGraph={{
          title,
          description,
          url,
          type: 'article',
          article: {
            publishedTime: publishedDate,
            modifiedTime: modifiedDate,
            tags,
          },
        }}
      />
      <ArticleJsonLd
        url={url}
        headline={title}
        datePublished={publishedDate}
        dateModified={modifiedDate}
        publisherLogo={`${siteUrl}${logo.publicURL}`}
        description={description}
        overrides={{
          '@type': 'BlogPosting',
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Accueil',
            item: `${siteUrl}/`,
          },

          {
            position: 2,
            name: 'Eco Gestes',
            item: `${siteUrl}/ecogestes`,
          },

          {
            position: 3,
            name: title,
            item: url,
          },
        ]}
      />

      {ecogesture.relatedQuestions?.length > 0 && (
        <FAQSeo questions={ecogesture.relatedQuestions} />
      )}

      <main className="mt-4 lg:mt-10">
        <div className="w-full max-w-screen-xl mx-auto">
          <div className="mb-4 hidden lg:grid grid-cols-4 gap-1">
            <nav
              className="col-span-3 text-xs lg:text-sm"
              aria-label="Breadcrumb"
            >
              <ol className="list-none p-0 inline-flex">
                <li className="flex items-center">
                  <Link to={`/`} title='Accueil'>
                    <FontAwesomeIcon icon={faHome} />
                  </Link>
                  <FontAwesomeIcon
                    className="fill-current w-3 h-3 mx-2"
                    icon={faChevronRight}
                  />
                </li>
                <li className="flex items-center">
                  <Link to={`/ecogestes/`} title="Eco Gestes">Eco Gestes</Link>
                  <FontAwesomeIcon
                    className="fill-current w-3 h-3 mx-2"
                    icon={faChevronRight}
                  />
                </li>
                <li>
                  <span className="text-gray-600" aria-current="page">
                    {title}
                  </span>
                </li>
              </ol>
            </nav>

            <div className="col-span-1 inline-flex text-xs justify-end">
              <FacebookShareButton
                url={url}
                quote={title}
                hashtag={`#${hashtags[0]}`}
                resetButtonStyle={false}
                className="cursor-pointer h-6 w-6 border-solid border border-gray-600 text-gray-600 hover:border-green-600 hover:text-green-600 font-bold mr-1 rounded-full"
              >
                <FontAwesomeIcon icon={faFacebookF} />
              </FacebookShareButton>
              <TwitterShareButton
                url={url}
                title={title}
                via={social.twitter}
                hashtags={hashtags}
                resetButtonStyle={false}
                related={[social.twitter]}
                className="cursor-pointer h-6 w-6 border-solid border border-gray-600 text-gray-600 hover:border-green-600 hover:text-green-600 font-bold mr-1 rounded-full"
              >
                <FontAwesomeIcon icon={faTwitter} />
              </TwitterShareButton>
              <LinkedinShareButton
                url={url}
                summary={description}
                source={siteName}
                resetButtonStyle={false}
                className="cursor-pointer h-6 w-6 border-solid border border-gray-600 text-gray-600 hover:border-green-600 hover:text-green-600 font-bold mr-1 rounded-full"
              >
                <FontAwesomeIcon icon={faLinkedinIn} />
              </LinkedinShareButton>
              <EmailShareButton
                subject={title}
                body={description}
                resetButtonStyle={false}
                className="cursor-pointer h-6 w-6 border-solid border border-gray-600 text-gray-600 hover:border-green-600 hover:text-green-600 font-bold mr-1 rounded-full"
              >
                <FontAwesomeIcon icon={faEnvelope} />
              </EmailShareButton>
            </div>
          </div>

          <div className="w-full max-w-screen-xl mx-auto">
            <div className="block lg:flex">
              <article className="p-4 w-full lg:w-3/4 inline-block">
                <header className="p-8 lg:py-12 bg-green-600 text-white text-center">
                  <div className="w-full max-w-screen-xl mx-auto">
                    <h1 className="font-bold text-2xl lg:text-4xl mb-2">{title}</h1>

                    <p className="text-lg lg:text-xl">{description.text}</p>
                  </div>
                </header>

                <div className="mt-2 lg:mt-12">
                  <RichContent
                    className="px-4 mt-8 prose prose-lg max-w-none"
                    content={ecogesture.body}
                  />

                  <div className="p-4 lg:px-8 m-2 lg:m-4 lg:my-8 bg-green-600 text-white text-center rounded">
                    <h3 className="font-bold text-xl uppercase mb-2">
                      Cet écogeste vous a intéressé ?
                    </h3>
                    <p>
                      Inscrivez-vous gratuitement à la Newsletter Planète Durable
                    </p>

                    <NewsletterForm className="my-4 mt-6 mx-auto" />
                  </div>
                </div>

                <div className="mt-4 lg:mt-8 w-full max-w-screen-xl mx-auto">
                  <ContentBottomBanner />
                </div>

                {ecogesture.relatedQuestions?.length > 0 && (
                  <div className="mt-4 lg:mt-8 w-full max-w-screen-xl mx-auto">
                    <div className="flex mb-4 px-4 lg:px-0 items-center justify-between">
                      <h2 className="font-bold text-2xl text-gray-800 px-1 mb-2">
                        En bref
                      </h2>
                    </div>

                    <section className="mb-4">
                      {ecogesture.relatedQuestions.map(({ id, name, answer }) => (
                        <QuestionAnswerCard key={`post-questions-${id}`} name={name} answer={answer} />
                      ))}
                    </section>
                  </div>
                )}

                <div className="mt-4 lg:mt-8 w-full max-w-screen-xl mx-auto">
                  <div className="flex mb-4 px-4 lg:px-0 items-center justify-between">
                    <h2 className="font-bold text-2xl text-gray-800 px-1 mb-2">
                      Encore plus de gestes pour préserver notre planète
                    </h2>
                  </div>

                  <section className="grid grid-cols-1 lg:grid-cols-3 gap-2 justify-start mb-4 lg:mb-0">
                    {allContentfulEcoGesture.edges.map(
                      ({ node: { id, ...ecogesture } }) => (
                        <div key={`ecogesture-others-${id}`} className="p-2">
                          <EcogestureCard
                            {...ecogesture}
                            loading="lazy"
                          />
                        </div>
                      )
                    )}
                  </section>
                </div>

                {allContentfulTipsheet.edges.length > 0 && (
                  <div className="mt-4 lg:mt-8 w-full max-w-screen-xl mx-auto">
                    <div className="flex mb-4 px-4 lg:px-0 items-center justify-between">
                      <h2 className="font-bold text-2xl text-gray-800 px-1 mb-2">
                        Guides qui pourraient vous intéresser
                      </h2>
                    </div>

                    <TipsheetsList tipsheets={allContentfulTipsheet.edges.map(({ node }) => node)} />
                  </div>
                )}
              </article>

              <Sidebar
                blogPosts={allContentfulBlogPost.edges}
                brands={allContentfulBrand.edges}
              />
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
}

Page.query = query

export default Page
